import { createColumnHelper } from '@tanstack/table-core';
import { DateTime, DurationObjectUnits } from 'luxon';
import React, { useEffect, useMemo,useState } from 'react';

import APIGateway from '@/api';
import { NativeTable } from '@/components/nativeTable/nativeTable';
import { useNativeTable } from '@/components/nativeTable/useNativeTable';
import { buildColumnClassString } from '@/components/table/table';
import TraceStepsData from '@/components/traceStepsData/traceStepsData';
import { useLocalZonePreference } from '@/hooks/useLocalZonePreference';
import { ChatMsgDebug, ChatMsgDebugIO } from '@/types/chat';
import { GetTracesArgs } from '@/types/traces';
import { OutputFormatEnum, TimeFormatter, TimeSourceTypeEnum } from '@/utils/timeFormatter';


type TraceData = {
  date: string;
  duration: number;
  inputs: string;
  outputs: string;
  agentStack: ChatMsgDebug[];
  isStepsRow?: boolean;
};

const columnHelper = createColumnHelper<TraceData>();

type Props = {
  agentId: string;
  editable: boolean;
  version: string;
};

export default function TraceView( props: Props ) {
  const {
    agentId,
    editable,
    version
  } = props;

  const [ traceData, settraceData ] = useState<TraceData[]>( [] );
  const [ useLocalZone ] = useLocalZonePreference();

  useEffect( () => {
    ( async() => {
      const table_rows: TraceData[] = [];

      const args: GetTracesArgs = {
        agentId,
        version
      };
      const { data: spans = [] } = await APIGateway.traces.tracesGet( args );

      for( const span of spans ) {
        let {
          custom: { traceloop: { association: { properties: {
            agent_input,
            agent_output
          } } } }
        } = span.attributes;

        const {
          custom: { traceloop: { association: { properties: {
            agent_stack
          } } } },
          end_timestamp,
          start_timestamp
        } = span.attributes;

        const spanStart = DateTime.fromISO( start_timestamp );
        const spanEnd = DateTime.fromISO( end_timestamp );
        const spanDuration: DurationObjectUnits = spanEnd.diff( spanStart ).toObject();

        const date = TimeFormatter( {
          sourceType: TimeSourceTypeEnum.EPOCH_MILLI,
          value: spanStart.toMillis().toString(),
          outputFormat: OutputFormatEnum.STRING_SINGLE,
          opts: { useLocalZone }
        } );

        if( agent_input ) {
          const inputObj = JSON.parse( agent_input ) as ChatMsgDebugIO;
          const inputConvo = inputObj.conversation;
          agent_input = inputConvo[inputConvo.length - 1].content + 'zzz';
        }
        if( agent_output ) {
          agent_output = 'na';
        }

        // if agent was invoked without debug flag, agent_stack won't exist
        const agent_stack_json: ChatMsgDebug[] = agent_stack
          ? JSON.parse( agent_stack )
          : [];

        const traceRow: TraceData = {
          date,
          duration: spanDuration.milliseconds!,
          inputs: agent_input,
          outputs: agent_output,
          agentStack: agent_stack_json
        };
        table_rows.push( traceRow );
      }

      settraceData( table_rows );
    } )();
  }, [ agentId, editable, version, useLocalZone ] );

  // Flatten trace data to include expanded rows for steps
  const expandedData = useMemo( () => {
    const expanded: Array<TraceData & { isStepsRow?: boolean }> = [];
    traceData.forEach( ( trace ) => {
      // Add main trace row
      expanded.push( trace );
      // Add steps row
      expanded.push( { ...trace, isStepsRow: true } );
    } );
    return expanded;
  }, [ traceData ] );

  const columns = useMemo( () => [
    columnHelper.display( {
      id: 'content',
      header: '',
      cell: info => {
        const row = info.row.original;
        if ( row.isStepsRow ) {
          return (
            <div style={ { gridColumn: '1 / -1' } }>
              <TraceStepsData agentStack={ row.agentStack } />
            </div>
          );
        }
        // For main rows, return null - individual columns will render
        return null;
      }
    } ),
    columnHelper.accessor( 'date', {
      header: 'Date',
      size: 200,
      cell: info => ( info.row.original.isStepsRow ? null : info.getValue() ),
      meta: {
        classStr: buildColumnClassString( { width: 20 } )
      }
    } ),
    columnHelper.accessor( 'duration', {
      header: 'Duration',
      size: 100,
      cell: info => ( info.row.original.isStepsRow ? null : info.getValue() ),
      meta: {
        classStr: buildColumnClassString( { width: 10 } )
      }
    } ),
    columnHelper.accessor( 'inputs', {
      header: 'Inputs',
      size: 350,
      cell: info => ( info.row.original.isStepsRow ? null : info.getValue() ),
      meta: {
        classStr: buildColumnClassString( { width: 35 } )
      }
    } ),
    columnHelper.accessor( 'outputs', {
      header: 'Outputs',
      size: 350,
      cell: info => ( info.row.original.isStepsRow ? null : info.getValue() ),
      meta: {
        classStr: buildColumnClassString( { width: 35 } )
      }
    } )
  ], [] );

  const table = useNativeTable( {
    data: expandedData,
    columns,
    enableSorting: false
  } );

  return <NativeTable table={ table } pagination="client" />;
}
